import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactNode,
  RefAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { Token as Color } from '../../../../export/brand0/colors';
import { Icon } from '../icon';
import { Image } from '../image';
import { cropText } from '../lib/crop-text';
import { Typography } from '../typography';

import styles from './button.scss';
import { IconSizes, isInvalidVariantForSize, SQUARE_SHAPE, VariantIconColorStates } from './config';
import { Ripple } from './ripple';
import type { Position, Shape, Size, Variant } from './types';

type ButtonIconProps = {
  doubleText?: string;
  icon?: string;
  iconPosition?: Position;
  label?: string;
  size?: number;
  iconFill?: Color;
  dataTestId?: string;
};

const COMPONENT_ID = 'modulor-button';

const ButtonIcon: React.FC<ButtonIconProps> = ({
  icon,
  doubleText,
  iconFill,
  dataTestId,
  size,
  iconPosition,
}) => {
  return (
    <>
      {icon && !doubleText && (
        <span
          className={classNames(styles.icon, {
            [styles['left']]: iconPosition === 'left',
            [styles['right']]: iconPosition === 'right',
          })}
          data-testid={`${dataTestId}-icon`}>
          <Icon name={icon} fill={iconFill} size={size} />
        </span>
      )}
    </>
  );
};

type ButtonImageProps = {
  doubleText?: string;
  image?: string | ReactNode;
  dataTestId?: string;
  size: number;
};

const ButtonImage: React.FC<ButtonImageProps> = ({ image, doubleText, dataTestId, size }) => {
  if (!image || doubleText) return null;

  return (
    <span className={classNames(styles.image)} data-testid={`${dataTestId}-image`}>
      {typeof image === 'string' ? (
        <Image name={image} width={`${size}`} height={`${size}`} />
      ) : (
        image
      )}
    </span>
  );
};

type LabelProps = {
  hasLabel?: boolean;
  disabled?: boolean;
  size?: Size;
  iconSize?: number;
  iconPosition?: Position;
  label?: string;
  icon?: string;
  image?: string | ReactNode;
  iconFill?: Color;
  doubleText?: string;
  children?: ReactNode;
  dataTestId?: string;
};

const MAX_LOW_LABEL = 20;

const Label: React.FC<LabelProps> = ({
  hasLabel,
  label = '',
  size,
  iconSize,
  iconPosition = 'left',
  disabled,
  icon,
  iconFill,
  image,
  doubleText,
  children,
  dataTestId,
}) => (
  <span
    className={classNames(styles['text-wrap'], {
      disabled: disabled,
      [styles.gap]: (label || children) && (icon || image),
    })}
    data-testid={`${dataTestId}-text-wrapper`}
    data-id={`${dataTestId}-text-wrapper`}>
    {iconPosition === 'left' && (
      <ButtonIcon
        icon={icon}
        iconFill={iconFill}
        dataTestId={dataTestId}
        doubleText={doubleText}
        size={iconSize}
        iconPosition={iconPosition}
      />
    )}
    <ButtonImage image={image} dataTestId={dataTestId} doubleText={doubleText} size={iconSize} />
    {hasLabel && (
      <Typography
        variant={size === 'low' ? 'advanced-button-low-title' : `advanced-button-${size}-title`}
        tag="span"
        dataTestId={`${dataTestId}-text`}>
        {size === 'low' && typeof children === 'string'
          ? cropText(label || children, MAX_LOW_LABEL)
          : label || children}
      </Typography>
    )}
    {iconPosition === 'right' && (
      <ButtonIcon
        icon={icon}
        iconFill={iconFill}
        dataTestId={dataTestId}
        doubleText={doubleText}
        size={iconSize}
        iconPosition={iconPosition}
      />
    )}
  </span>
);

export type ButtonProps = {
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  variant?: Variant;
  doubleText?: string;
  loading?: boolean;
  size?: Size;
  iconSize?: number;
  iconPosition?: Position;
  image?: string | ReactNode;
  icon?: string;
  doubleInverted?: boolean;
  label?: string;
  dataTestId?: string;
  shape?: Shape;
  disableRipple?: boolean;
};

type Props = RefAttributes<HTMLDivElement & HTMLButtonElement> &
  HTMLAttributes<HTMLDivElement & HTMLButtonElement> &
  ButtonProps;

export const Button: ForwardRefExoticComponent<Props> = forwardRef<
  HTMLDivElement & HTMLButtonElement,
  Props
>(
  (
    {
      type = 'button',
      disabled,
      variant = 'primary',
      doubleText,
      loading,
      iconSize,
      iconPosition = 'left',
      size = 'regular',
      icon,
      image,
      doubleInverted,
      label,
      children,
      onClick,
      dataTestId = COMPONENT_ID,
      className,
      shape,
      disableRipple,
      ...rest
    },
    ref,
  ) => {
    const [actualVariant, setActualVariant] = useState(variant);

    useEffect(() => {
      if (isInvalidVariantForSize(size, variant)) {
        console.warn(
          `Modulor: Variant "${variant}" is not correct for size "${size}", please, refer to Modulor design docs. Automatically switched to "primary" variant`,
        );

        setActualVariant('primary');

        return;
      }

      setActualVariant(variant);
    }, [variant, size]);

    const containerClasses = classNames(className, styles['button-container'], {
      [styles['button-container-disabled']]: disabled,
    });

    const hasLabel = useMemo(
      () => (shape === SQUARE_SHAPE ? false : Boolean(label || children)),
      [shape, label, children],
    );

    const btnClasses = classNames(
      styles.button,
      styles[actualVariant],
      styles[size],
      styles[shape],
      {
        [styles['with-icon']]: (icon || image) && hasLabel,
        [styles['with-icon-right']]: (icon || image) && iconPosition === 'right' && hasLabel,
        [styles.loading]: loading && !disabled,
        [styles.circle]: size === 'low' && !hasLabel,
        [styles['disable-ripple']]: disableRipple,
      },
    );

    const calculatedIconSize = useMemo(() => {
      const dynamicSize = size === 'low' ? IconSizes.low : IconSizes.default;

      if (shape === SQUARE_SHAPE) {
        return dynamicSize;
      }

      return iconSize || dynamicSize;
    }, [shape, iconSize, size]);

    const onClickHandler = useCallback(
      (e: React.MouseEvent<HTMLDivElement & HTMLButtonElement, MouseEvent>) => {
        if (loading || disabled) {
          return;
        }

        if (typeof onClick === 'function') {
          onClick(e);
        }
      },
      [onClick, loading, disabled],
    );

    const disabledIconColor: Color = useMemo(
      () => (VariantIconColorStates[actualVariant]?.disabled || 'icon-disabled') as Color,
      [actualVariant],
    );

    const defaultIconColor: Color = useMemo(
      () => (VariantIconColorStates[actualVariant]?.default || 'icon-primary-control') as Color,
      [actualVariant],
    );

    const loaderIconColor: Color = useMemo(
      () => (VariantIconColorStates[actualVariant]?.loading || 'icon-primary-control') as Color,
      [actualVariant],
    );

    const iconColor: Color = useMemo(
      () => (disabled ? disabledIconColor : defaultIconColor),
      [disabled, disabledIconColor, defaultIconColor],
    );

    useEffect(() => {
      if (variant === 'white') {
        console.warn('Modulor: Variant “white” is deprecated. Use variant “always_white” instead.');
      }

      if (typeof icon === 'object') {
        console.warn(
          'Modulor: Passing "icon" as object is deprecated. Use icon token as string instead.',
        );
      }

      if (label) {
        console.warn(
          'Modulor: Property "label" is deprecated and will be removed in 2.0.0 release, place your text or custom label component directly inside Button component.',
        );
      }
    }, [icon, label, variant]);

    return (
      <div
        data-component-name={COMPONENT_ID}
        data-testid={dataTestId}
        data-id={dataTestId}
        ref={ref}
        className={containerClasses}
        {...rest}>
        <button
          type={type}
          data-testid={`${dataTestId}-button`}
          onClick={onClickHandler}
          disabled={disabled}
          className={btnClasses}>
          {!disableRipple && (
            <Ripple
              data-testid={`${dataTestId}-ripple-effect-wrapper`}
              data-id={`${dataTestId}-ripple-effect-wrapper`}
            />
          )}

          {loading && !disabled && (
            <div
              className={styles.loader}
              data-testid={`${dataTestId}-loader-wrapper`}
              data-id={`${dataTestId}-loader-wrapper`}>
              <Icon
                name="loader"
                fill={loaderIconColor}
                size={calculatedIconSize}
                className={styles['loader-icon']}
              />
            </div>
          )}

          {!doubleInverted && (
            <Label
              hasLabel={hasLabel}
              label={label}
              size={size}
              iconSize={calculatedIconSize}
              iconPosition={iconPosition}
              disabled={disabled}
              icon={icon}
              image={image}
              dataTestId={`${dataTestId}-label`}
              iconFill={iconColor}
              doubleText={doubleText}>
              {children}
            </Label>
          )}

          {size !== 'low' && doubleText && (
            <div
              className={styles['double-text']}
              data-testid={`${dataTestId}-double-text-wrapper`}
              data-id={`${dataTestId}-double-text-wrapper`}>
              <Typography
                variant={`advanced-button-${size}-subtitle`}
                tag="span"
                data-testid={`${dataTestId}-double-text`}>
                {doubleText}
              </Typography>
            </div>
          )}

          {doubleInverted && (
            <Label
              hasLabel={hasLabel}
              label={label}
              size={size}
              iconSize={calculatedIconSize}
              iconPosition={iconPosition}
              disabled={disabled}
              icon={icon}
              iconFill={iconColor}
              dataTestId={`${dataTestId}-label`}
              doubleText={doubleText}>
              {children}
            </Label>
          )}
        </button>
      </div>
    );
  },
);

Button.displayName = 'Button';
